import React, { useEffect, useState, useCallback } from "react";
import { ArrowRight, Save } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api from "@/lib/api";
import { POPUP_ICONS } from "@/components/SitePopup";

const inputCls = "rounded-xl border-brand/20 bg-white h-10 focus-visible:ring-brand";
const lightCard = "rounded-2xl bg-white border border-brand/10 p-5 shadow-[0_8px_30px_rgba(134,77,99,0.06)]";

// Dev editor for the site-wide popup
export const DevPopup = () => {
  const { t } = useLang();
  const p = t.popup;
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/popup").then((r) => setForm(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  if (!form) return null;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/popup", form);
      sessionStorage.removeItem("cfm_popup_seen");
      toast.success(p.saved);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const Icon = POPUP_ICONS[form.icon] || POPUP_ICONS.sparkles;

  return (
    <div className="grid gap-5 lg:grid-cols-2" data-testid="dev-popup">
      {/* Form */}
      <div className={`${lightCard} space-y-4`}>
        <div>
          <p className="font-heading text-xl font-bold text-brand-ink">{p.title}</p>
          <p className="text-xs text-brand-ink/50">{p.subtitle}</p>
        </div>

        <button
          onClick={() => setForm({ ...form, enabled: !form.enabled })}
          className="flex items-center justify-between w-full rounded-2xl bg-stone-alt/40 px-4 py-3.5 hover:bg-stone-alt/70 transition-colors"
          data-testid="popup-enabled-toggle"
        >
          <span className="text-sm font-bold text-brand-ink">{p.enabled}</span>
          <span className={`relative h-6 w-11 rounded-full transition-colors ${form.enabled ? "bg-bamboo" : "bg-brand-ink/20"}`}>
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${form.enabled ? "left-[22px]" : "left-0.5"}`} />
          </span>
        </button>

        <div>
          <Label className="text-brand-ink font-semibold">{p.icon}</Label>
          <div className="mt-2 flex gap-2 flex-wrap" data-testid="popup-icon-picker">
            {Object.entries(POPUP_ICONS).map(([key, Ic]) => (
              <button
                key={key}
                onClick={() => setForm({ ...form, icon: key })}
                className={`grid place-items-center h-11 w-11 rounded-xl border transition-colors no-a11y-underline ${
                  form.icon === key ? "bg-brand text-brand-cream border-brand" : "bg-stone-alt/40 text-brand-ink/60 border-brand/10 hover:border-brand/40"
                }`}
                data-testid={`popup-icon-${key}`}
                aria-label={key}
              >
                <Ic className="h-5 w-5" />
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-brand-ink font-semibold">{p.ptitle}</Label>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={`${inputCls} mt-1`} data-testid="popup-title" />
        </div>
        <div>
          <Label className="text-brand-ink font-semibold">{p.text}</Label>
          <Textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} className="rounded-xl border-brand/20 min-h-[90px] mt-1" data-testid="popup-text" />
        </div>

        <div>
          <Label className="text-brand-ink font-semibold">{p.btn}</Label>
          <Select value={form.btn_kind} onValueChange={(v) => setForm({ ...form, btn_kind: v })}>
            <SelectTrigger className={`${inputCls} mt-1`} data-testid="popup-btn-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{p.btn_none}</SelectItem>
              <SelectItem value="section">{p.btn_section}</SelectItem>
              <SelectItem value="url">{p.btn_url}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {form.btn_kind !== "none" && (
          <>
            <div>
              <Label className="text-brand-ink font-semibold">{p.btn_label}</Label>
              <Input value={form.btn_label} onChange={(e) => setForm({ ...form, btn_label: e.target.value })} className={`${inputCls} mt-1`} data-testid="popup-btn-label" />
            </div>
            {form.btn_kind === "section" ? (
              <div>
                <Label className="text-brand-ink font-semibold">{p.section}</Label>
                <Select value={form.btn_section} onValueChange={(v) => setForm({ ...form, btn_section: v })}>
                  <SelectTrigger className={`${inputCls} mt-1`} data-testid="popup-btn-section"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(p.sections).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <Label className="text-brand-ink font-semibold">{p.url}</Label>
                <Input value={form.btn_url} onChange={(e) => setForm({ ...form, btn_url: e.target.value })} placeholder="https://..." className={`${inputCls} mt-1`} data-testid="popup-btn-url" />
              </div>
            )}
          </>
        )}

        <Button onClick={save} disabled={saving} className="w-full rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-11" data-testid="popup-save">
          <Save className="h-4 w-4 mr-2" /> {p.save}
        </Button>
      </div>

      {/* Live preview */}
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-brand-ink/50 mb-2">{p.preview}</p>
        <div className="rounded-[2rem] bg-brand-ink/10 p-6 grid place-items-center min-h-[380px]">
          <div className="relative max-w-sm w-full bg-white rounded-[2rem] border border-brand/10 shadow-[0_30px_80px_rgba(42,37,34,0.18)] p-8 text-center" data-testid="popup-preview">
            <span className="mx-auto grid place-items-center h-16 w-16 rounded-full bg-brand-soft text-brand mb-5">
              <Icon className="h-7 w-7" />
            </span>
            {form.title && <h2 className="font-heading text-3xl font-bold text-brand-ink">{form.title}</h2>}
            {form.text && <p className="mt-3 text-brand-muted leading-relaxed whitespace-pre-line text-sm">{form.text}</p>}
            {form.btn_kind !== "none" && form.btn_label && (
              <span className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand text-brand-cream font-bold px-7 h-12">
                {form.btn_label} <ArrowRight className="h-4 w-4" />
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DevPopup;
